import { collection, getDocs } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { db } from "../../firebase";

export default function TeamForm({ selectedRace, canUseMemberFreeRegistration = false }) {
  const [selected, setSelected] = useState(null);
  const [teamsMap, setTeamsMap] = useState(() => new Map());
  const [teamsArray, setTeamsArray] = useState([]);
  const [isTeamCodeInvalid, setIsTeamCodeInvalid] = useState(false);
  const [isTeamSelectionInvalid, setIsTeamSelectionInvalid] = useState(false);
  const [newNameInvalid, setNewNameInvalid] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [showTeamList, setShowTeamList] = useState(false);
  const [teamSearch, setTeamSearch] = useState("");
  const teamNameRef = useRef();
  const teamCodeRef = useRef();
  const newTeamNameRef = useRef();
  const newTeamCodeRef = useRef();
  const normalizedTeamSearch = teamSearch.trim().toLowerCase();
  const filteredTeams = normalizedTeamSearch
    ? teamsArray.filter((team) => team.name.toLowerCase().includes(normalizedTeamSearch))
    : teamsArray;

  useEffect(() => {
    async function fetchTeams() {
      setSelectedTeamName("");
      setTeamSearch("");
      setShowTeamList(false);
      setIsTeamSelectionInvalid(false);
      if (!selectedRace) {
        setTeamsMap(new Map());
        setTeamsArray([]);
        return;
      }
      const newTeamsMap = new Map();
      const newTeamsArray = [];
      const teamDocs = await getDocs(collection(db, "races", selectedRace, "teams"));
      teamDocs.forEach((team) => {
        newTeamsMap.set(team.id, team.data());
        newTeamsArray.push({ name: team.id, code: team.data().code });
        // console.log(newTeamsMap, newTeamsArray);
      });
      setTeamsMap(newTeamsMap);
      setTeamsArray(newTeamsArray.sort((a, b) => a.name.localeCompare(b.name)));
    }
    fetchTeams();
    return () => {};
  }, [selectedRace]);

  return (
    <Form.Group className="mt-3">
      <h3 className="mb-3 text-2xl text-left">Teams</h3>
      {!selectedRace && (
        <p className="mb-3 rounded-md border border-[#D8DEE6] bg-[#F8FBFD] px-3 py-2 text-sm font-semibold text-[#5E6470]">
          Please choose a distance
        </p>
      )}
      <Form.Group className="grid grid-cols-1 items-start gap-2 lg:grid-cols-3 lg:gap-4">
        <TeamChoiceCard
          id="newTeam"
          value="newTeam"
          selected={selected}
          onSelect={setSelected}
          imageSrc="/img/Create-team.png"
          imageAlt="Create a team"
          title="Create a Team"
          description="Form a team, invite friends!"
        >
          <Form.Group className="mt-6 text-left">
            <Form.Label htmlFor="teamName" className="mb-1 text-sm font-semibold text-[#2F3338]">
              Team Name
            </Form.Label>
            <Form.Control
              id="teamName"
              name="teamName"
              type="text"
              placeholder="My awesome team name"
              ref={newTeamNameRef}
              isInvalid={newNameInvalid}
              pattern="[\w ]+"
              title="Only letters and numbers accepted"
              required
              className="!rounded-md"
            />
            <Form.Control.Feedback type="invalid">That team name is already taken.</Form.Control.Feedback>

            <Form.Label htmlFor="teamCode" className="mb-1 mt-3 text-sm font-semibold text-[#2F3338]">
              Create a Code
            </Form.Label>
            <Form.Control
              id="teamCode"
              name="teamCode"
              type="text"
              placeholder="Secret team code!"
              ref={newTeamCodeRef}
              required
              className="!rounded-md"
            />
          </Form.Group>
        </TeamChoiceCard>

        <TeamChoiceCard
          id="existingTeam"
          value="existingTeam"
          selected={selected}
          onSelect={(value) => {
            setSelected(value);
            setShowTeamList(true);
          }}
          imageSrc="/img/join-team.png"
          imageAlt="Join a team"
          title="Join a Team"
          description="Search and enter code from your team captain."
        >
          <Form.Group className="mt-6 text-left">
            <Form.Label htmlFor="teamSearch" className="mb-1 text-sm font-semibold text-[#2F3338]">
              Select a Team
            </Form.Label>
            <input type="hidden" name="teamName" value={selectedTeamName} ref={teamNameRef} readOnly />
            {selectedTeamName && !showTeamList ? (
              <div className="rounded-md border border-[#D8DEE6] bg-[#F8FBFD] px-3 py-2">
                <p className="m-0 text-xs font-semibold text-[#5E6470]">Selected team</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="m-0 text-sm font-semibold leading-snug text-[#111318]">{selectedTeamName}</p>
                  <button
                    type="button"
                    className="shrink-0 text-xs font-semibold text-[#006BC1] underline"
                    onClick={() => {
                      setSelectedTeamName("");
                      setTeamSearch("");
                      setShowTeamList(true);
                      setIsTeamCodeInvalid(false);
                      setIsTeamSelectionInvalid(false);
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Form.Control
                  id="teamSearch"
                  type="text"
                  placeholder="Search or select a team"
                  value={teamSearch}
                  isInvalid={isTeamSelectionInvalid}
                  onChange={(event) => {
                    const nextSearch = event.target.value;
                    setTeamSearch(nextSearch);
                    setShowTeamList(true);
                    setIsTeamSelectionInvalid(false);
                    if (nextSearch !== selectedTeamName) {
                      setSelectedTeamName("");
                    }
                  }}
                  onFocus={() => setShowTeamList(true)}
                  onClick={() => setShowTeamList(true)}
                  className={"!rounded-md " + (showTeamList ? "!rounded-b-none" : "")}
                />
                <Form.Control.Feedback type="invalid">Please select a team from the list.</Form.Control.Feedback>
                {showTeamList && (
                  <>
                    <div className="max-h-[6.75rem] overflow-y-auto rounded-b-md border border-t-0 border-[#E0E0E0] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                      {filteredTeams.length > 0 ? (
                        <>
                          {filteredTeams.map((team) => {
                            const isSelectedTeam = selectedTeamName === team.name;
                            return (
                              <button
                                key={team.name}
                                type="button"
                                className={
                                  "block w-full px-3 py-2 text-left text-sm leading-snug transition-colors hover:bg-[#F1F7FC] " +
                                  (isSelectedTeam ? "bg-[#EAF4FC] font-semibold text-[#006BC1]" : "text-[#111318]")
                                }
                                onClick={() => {
                                  setSelectedTeamName(team.name);
                                  setTeamSearch(team.name);
                                  setShowTeamList(false);
                                  setIsTeamSelectionInvalid(false);
                                }}
                              >
                                {team.name}
                              </button>
                            );
                          })}
                          {filteredTeams.length > 3 && (
                            <div className="sticky bottom-0 border-t border-[#E0E0E0] bg-[#F8FBFD] px-3 py-1 text-center text-xs font-semibold text-[#5E6470]">
                              Scroll for more teams
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="m-0 px-3 py-2 text-sm text-[#5E6470]">
                          {teamsArray.length > 0 ? "No teams match your search." : "No teams have been created yet."}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {selectedTeamName && !showTeamList && (
              <>
                <Form.Label htmlFor="teamCode" className="mb-1 mt-3 text-sm font-semibold text-[#2F3338]">
                  Enter your Team Code
                </Form.Label>
                <Form.Control
                  id="teamCode"
                  name="teamCode"
                  type="text"
                  placeholder="Your team's secret code"
                  isInvalid={isTeamCodeInvalid}
                  required
                  ref={teamCodeRef}
                  className="!rounded-md"
                />
                <Form.Control.Feedback type="invalid">That code does not match this team.</Form.Control.Feedback>
              </>
            )}
          </Form.Group>
        </TeamChoiceCard>

        <TeamChoiceCard
          id="noTeam"
          value="noTeam"
          selected={selected}
          onSelect={() => setSelected("")}
          imageSrc="/img/solo.png"
          imageAlt="Individual entry"
          title="Go Solo"
          description="Not joining a team. Complete the challenge on your own."
        />
      </Form.Group>
      <Form.Group className="mx-auto w-full my-3 pt-3">
        <Button
          size=""
          className="w-full mx-auto py-2"
          type="submit"
          onClick={(e) => {
            setIsTeamCodeInvalid(false);
            setIsTeamSelectionInvalid(false);
            setNewNameInvalid(false);

            if (newTeamNameRef.current) {
              newTeamNameRef.current.value = newTeamNameRef.current.value.replace(/^\s+/, "").replace(/\s+$/, "");
              if (teamsMap.get(newTeamNameRef.current.value)) {
                e.preventDefault();
                setNewNameInvalid(true);
              }
            }
            if (newTeamCodeRef.current) {
              newTeamCodeRef.current.value = newTeamCodeRef.current.value.replace(/^\s+/, "").replace(/\s+$/, "");
            }
            if (selected === "existingTeam") {
              const existingTeamName = teamNameRef.current?.value || "";
              if (!existingTeamName) {
                e.preventDefault();
                setIsTeamSelectionInvalid(true);
                return;
              }
              if (!teamCodeRef.current) {
                return;
              }
              teamCodeRef.current.value = teamCodeRef.current.value.replace(/^\s+/, "").replace(/\s+$/, "");
              if (teamsMap.get(existingTeamName)?.code !== teamCodeRef.current.value) {
                // console.log("TRIED");
                e.preventDefault();
                setIsTeamCodeInvalid(true);
              }
            }
          }}
        >
          {canUseMemberFreeRegistration ? "Claim My Spot" : "Continue"}
        </Button>
      </Form.Group>
    </Form.Group>
  );
}

function TeamChoiceCard({
  id,
  value,
  selected,
  onSelect,
  imageSrc,
  imageAlt,
  title,
  description,
  children,
}) {
  const isSelected = selected === value || (value === "noTeam" && selected === "");

  return (
    <Form.Check type="radio" className="relative !mb-0 h-full !pl-0">
      <Form.Check.Input
        className="peer sr-only"
        type="radio"
        name="teamChoice"
        id={id}
        value={value}
        checked={isSelected}
        onChange={() => onSelect(value)}
        required
      />
      <div
        className="relative mb-0 flex h-full cursor-pointer flex-col rounded-xl border-2 border-[#E0E0E0] bg-white px-4 pb-4 pt-3 text-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 peer-checked:border-[#006BC1] peer-checked:shadow-[0_0_0_1px_#006BC1,0_2px_8px_rgba(0,0,0,0.08)] peer-focus-visible:ring-4 peer-focus-visible:ring-[#006BC1]/15"
        onClick={() => onSelect(value)}
      >
        <Form.Label htmlFor={id} className="absolute inset-0 z-10 m-0 cursor-pointer">
          <span className="sr-only">{title}</span>
        </Form.Label>
        <span
          className={
            "pointer-events-none absolute right-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white transition-colors duration-200 after:block after:h-2.5 after:w-2.5 after:rounded-full after:transition-colors after:duration-200 " +
            (isSelected
              ? "border-[#006BC1] after:bg-[#006BC1]"
              : "border-[#B0B0B0] after:bg-transparent")
          }
        >
        </span>
        <div className="pointer-events-none relative z-20 grid w-full flex-1 grid-cols-[3.5rem,1fr] items-center gap-3 pr-8 text-left lg:flex lg:flex-col lg:gap-0 lg:pr-0 lg:text-center">
          <img className="h-12 w-12 object-contain lg:mb-2 lg:h-16 lg:w-16" src={imageSrc} alt={imageAlt} />
          <div>
            <p className="mb-1 text-lg font-extrabold leading-tight tracking-normal text-[#111318] lg:text-xl">{title}</p>
            <p className="m-0 max-w-[13rem] text-xs font-semibold leading-snug text-[#5E6470]">{description}</p>
          </div>
        </div>
        {children && isSelected && (
          <div className="relative z-20 cursor-default" onClick={(event) => event.stopPropagation()}>
            {children}
          </div>
        )}
      </div>
    </Form.Check>
  );
}
